/**
 * RedemptionConfigEditor Component
 *
 * Editor for RedemptionConfig with form/JSON toggle.
 * Supports manual, API, and instant redemption types.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Input, Select, TextArea } from '@/components/common/FormField';
import type {
  RedemptionConfig,
  RedemptionType,
  RedemptionApiConfig,
  PreRedemptionHandlerType,
} from '@/app/types/rewards';
import {
  REDEMPTION_TYPE_OPTIONS,
  PRE_REDEMPTION_HANDLER_OPTIONS,
} from '@/app/types/rewards';

import './RedemptionConfigEditor.scss';

interface RedemptionConfigEditorProps {
  value: RedemptionConfig | null;
  onChange: (value: RedemptionConfig | null) => void;
}

type EditorMode = 'structured' | 'json';

const DEFAULT_API_CONFIG: RedemptionApiConfig = {
  base_url: { nonprod: '', prod: '' },
  endpoint: '',
  method: 'POST',
  headers: {},
  payload: {},
  retry: { max_attempts: 3, backoff_ms: [1000, 2000, 5000] },
  success: { status_codes: [200, 201, 202], response_mapping: {} },
};

export const RedemptionConfigEditor: React.FC<RedemptionConfigEditorProps> = ({
  value,
  onChange,
}) => {
  const [mode, setMode] = useState<EditorMode>('structured');
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const [headersText, setHeadersText] = useState('');
  const [payloadText, setPayloadText] = useState('');
  const [headersError, setHeadersError] = useState<string | null>(null);
  const [payloadError, setPayloadError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === 'structured' && value?.type === 'api') {
      setHeadersText(
        value.api?.headers && Object.keys(value.api.headers).length > 0
          ? JSON.stringify(value.api.headers, null, 2)
          : ''
      );
      setPayloadText(
        value.api?.payload && Object.keys(value.api.payload).length > 0
          ? JSON.stringify(value.api.payload, null, 2)
          : ''
      );
      setHeadersError(null);
      setPayloadError(null);
    }
  // Only re-sync when switching to structured mode or when the type changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, value?.type]);

  // Initialize JSON text when value changes
  useEffect(() => {
    if (mode === 'json') {
      setJsonText(value ? JSON.stringify(value, null, 2) : '');
    }
  }, [value, mode]);

  // Handle mode toggle
  const handleModeChange = (newMode: EditorMode) => {
    if (newMode === 'json') {
      setJsonText(value ? JSON.stringify(value, null, 2) : '');
      setJsonError(null);
    }
    setMode(newMode);
  };

  // Handle JSON text changes
  const handleJsonChange = (text: string) => {
    setJsonText(text);
    if (!text.trim()) {
      setJsonError(null);
      onChange(null);
      return;
    }
    try {
      const parsed = JSON.parse(text);
      setJsonError(null);
      onChange(parsed as RedemptionConfig);
    } catch {
      setJsonError('Invalid JSON');
    }
  };

  // Update type
  const updateType = useCallback(
    (type: RedemptionType) => {
      const updated: RedemptionConfig = {
        type,
        provider: value?.provider || '',
        ...(type === 'api' && { api: value?.api || DEFAULT_API_CONFIG }),
        ...(value?.pre_redemption && { pre_redemption: value.pre_redemption }),
      };
      onChange(updated);
    },
    [value, onChange]
  );

  // Update provider
  const updateProvider = useCallback(
    (provider: string) => {
      if (!value) {
        onChange({ type: 'manual', provider });
        return;
      }
      onChange({ ...value, provider });
    },
    [value, onChange]
  );

  // Update API config field
  const updateApiField = useCallback(
    <K extends keyof RedemptionApiConfig>(
      key: K,
      fieldValue: RedemptionApiConfig[K]
    ) => {
      if (!value || value.type !== 'api') return;
      const api = { ...(value.api || DEFAULT_API_CONFIG), [key]: fieldValue };
      onChange({ ...value, api });
    },
    [value, onChange]
  );

  // Update nested base_url field
  const updateBaseUrl = useCallback(
    (env: 'nonprod' | 'prod', url: string) => {
      if (!value || value.type !== 'api') return;
      const baseUrl = {
        ...(value.api?.base_url || { nonprod: '', prod: '' }),
        [env]: url,
      };
      updateApiField('base_url', baseUrl);
    },
    [value, updateApiField]
  );

  const handleHeadersChange = useCallback(
    (text: string) => {
      setHeadersText(text);
      if (!value || value.type !== 'api') return;
      if (!text.trim()) {
        setHeadersError(null);
        updateApiField('headers', {});
        return;
      }
      try {
        const headers = JSON.parse(text);
        setHeadersError(null);
        updateApiField('headers', headers);
      } catch {
        setHeadersError('Invalid JSON');
      }
    },
    [value, updateApiField]
  );

  const handlePayloadChange = useCallback(
    (text: string) => {
      setPayloadText(text);
      if (!value || value.type !== 'api') return;
      if (!text.trim()) {
        setPayloadError(null);
        updateApiField('payload', {});
        return;
      }
      try {
        const payload = JSON.parse(text);
        setPayloadError(null);
        updateApiField('payload', payload);
      } catch {
        setPayloadError('Invalid JSON');
      }
    },
    [value, updateApiField]
  );

  // Update pre-redemption handler
  const updatePreRedemptionHandler = useCallback(
    (handlerValue: string) => {
      if (!value) return;
      if (!handlerValue) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { pre_redemption: _, ...rest } = value;
        onChange(rest);
        return;
      }
      onChange({
        ...value,
        pre_redemption: { handler: handlerValue as PreRedemptionHandlerType },
      });
    },
    [value, onChange]
  );

  // Clear configuration
  const handleClear = () => {
    onChange(null);
    setJsonText('');
    setJsonError(null);
  };

  // Initialize with default if not set
  const initializeConfig = () => {
    onChange({ type: 'manual', provider: '' });
  };

  const typeOptions = REDEMPTION_TYPE_OPTIONS.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));

  const methodOptions = [
    { value: 'GET', label: 'GET' },
    { value: 'POST', label: 'POST' },
    { value: 'PUT', label: 'PUT' },
    { value: 'PATCH', label: 'PATCH' },
  ];

  return (
    <div className="redemption-config-editor">
      {/* Mode Toggle */}
      <div className="mode-toggle">
        <button
          type="button"
          className={`toggle-btn ${mode === 'structured' ? 'active' : ''}`}
          onClick={() => handleModeChange('structured')}
        >
          Structured Form
        </button>
        <button
          type="button"
          className={`toggle-btn ${mode === 'json' ? 'active' : ''}`}
          onClick={() => handleModeChange('json')}
        >
          JSON Editor
        </button>
      </div>

      {mode === 'json' ? (
        /* JSON Editor Mode */
        <div className="json-editor">
          <textarea
            className={`json-textarea ${jsonError ? 'error' : ''}`}
            value={jsonText}
            onChange={(e) => handleJsonChange(e.target.value)}
            placeholder='{"type": "manual", "provider": "benepass"}'
            spellCheck={false}
          />
          {jsonError && <div className="json-error">{jsonError}</div>}
        </div>
      ) : (
        /* Structured Form Mode */
        <div className="structured-editor">
          {!value ? (
            <div className="empty-state">
              <p>No redemption configuration set.</p>
              <button
                type="button"
                className="btn-initialize"
                onClick={initializeConfig}
              >
                + Configure Redemption
              </button>
            </div>
          ) : (
            <>
              {/* Basic Config */}
              <div className="form-section">
                <h4>Basic Configuration</h4>
                <div className="form-grid">
                  <Select
                    label="Redemption Type"
                    value={value.type}
                    onChange={(val) => updateType(val as RedemptionType)}
                    options={typeOptions}
                  />
                  <Input
                    label="Provider"
                    value={value.provider}
                    onChange={updateProvider}
                    placeholder="e.g., benepass, lore-note"
                    hint="Identifier for the fulfillment provider"
                  />
                </div>
              </div>

              {/* Pre-Redemption Handler */}
              <div className="form-section">
                <h4>Pre-Redemption Data Routing</h4>
                <div className="form-grid">
                  <Select
                    label="Pre-Redemption Handler"
                    value={value.pre_redemption?.handler || ''}
                    onChange={updatePreRedemptionHandler}
                    options={PRE_REDEMPTION_HANDLER_OPTIONS.map((opt) => ({
                      value: opt.value,
                      label: opt.label,
                    }))}
                    hint="Determines where pre-redemption form data is stored"
                  />
                </div>
              </div>

              {/* API Config (only shown for type="api") */}
              {value.type === 'api' && (
                <div className="form-section api-section">
                  <h4>API Configuration</h4>
                  
                  {/* Base URLs */}
                  <div className="subsection">
                    <h5>Base URLs</h5>
                    <div className="form-grid">
                      <Input
                        label="Non-Production URL"
                        value={value.api?.base_url?.nonprod || ''}
                        onChange={(val) => updateBaseUrl('nonprod', val)}
                        placeholder="https://api-staging.example.com"
                      />
                      <Input
                        label="Production URL"
                        value={value.api?.base_url?.prod || ''}
                        onChange={(val) => updateBaseUrl('prod', val)}
                        placeholder="https://api.example.com"
                      />
                    </div>
                  </div>

                  {/* Endpoint Config */}
                  <div className="subsection">
                    <h5>Endpoint</h5>
                    <div className="form-grid">
                      <Input
                        label="Endpoint Path"
                        value={value.api?.endpoint || ''}
                        onChange={(val) => updateApiField('endpoint', val)}
                        placeholder="/v1/rewards/fulfill"
                      />
                      <Select
                        label="HTTP Method"
                        value={value.api?.method || 'POST'}
                        onChange={(val) =>
                          updateApiField('method', val as 'GET' | 'POST' | 'PUT' | 'PATCH')
                        }
                        options={methodOptions}
                      />
                    </div>
                  </div>

                  {/* Headers */}
                  <div className="subsection">
                    <h5>Headers</h5>
                    <TextArea
                      label="Request Headers (JSON)"
                      value={headersText}
                      onChange={handleHeadersChange}
                      // eslint-disable-next-line no-template-curly-in-string
                      placeholder='{"Authorization": "Bearer ${jwt_token}"}'
                      rows={3}
                      // eslint-disable-next-line no-template-curly-in-string
                      hint="Template variables: ${jwt_token}, ${ref_user_id}, etc."
                      error={headersError || undefined}
                    />
                  </div>

                  {/* Payload */}
                  <div className="subsection">
                    <h5>Payload</h5>
                    <TextArea
                      label="Request Payload (JSON)"
                      value={payloadText}
                      onChange={handlePayloadChange}
                      // eslint-disable-next-line no-template-curly-in-string
                      placeholder='{"user_id": "${ref_user_id}", "transaction_id": "${transaction_id}"}'
                      rows={6}
                      // eslint-disable-next-line no-template-curly-in-string
                      hint="Available variables: ${ref_user_id}, ${transaction_id}, ${fulfillment_id}, ${reward_key}, ${point_value}, ${dollar_value}, ${completion_points}"
                      error={payloadError || undefined}
                    />
                  </div>

                  {/* Retry Config */}
                  <div className="subsection">
                    <h5>Retry Configuration</h5>
                    <div className="form-grid">
                      <Input
                        label="Max Attempts"
                        type="number"
                        value={(value.api?.retry?.max_attempts || 3).toString()}
                        onChange={(val) =>
                          updateApiField('retry', {
                            ...(value.api?.retry || { max_attempts: 3, backoff_ms: [1000, 2000, 5000] }),
                            max_attempts: parseInt(val, 10) || 3,
                          })
                        }
                      />
                      <Input
                        label="Backoff (ms, comma-separated)"
                        value={(value.api?.retry?.backoff_ms || [1000, 2000, 5000]).join(', ')}
                        onChange={(val) =>
                          updateApiField('retry', {
                            ...(value.api?.retry || { max_attempts: 3, backoff_ms: [] }),
                            backoff_ms: val
                              .split(',')
                              .map((s) => parseInt(s.trim(), 10))
                              .filter((n) => !isNaN(n)),
                          })
                        }
                        placeholder="1000, 2000, 5000"
                      />
                    </div>
                  </div>

                  {/* Success Config */}
                  <div className="subsection">
                    <h5>Success Handling</h5>
                    <div className="form-grid">
                      <Input
                        label="Success Status Codes (comma-separated)"
                        value={(value.api?.success?.status_codes || [200, 201, 202]).join(', ')}
                        onChange={(val) =>
                          updateApiField('success', {
                            ...(value.api?.success || { status_codes: [] }),
                            status_codes: val
                              .split(',')
                              .map((s) => parseInt(s.trim(), 10))
                              .filter((n) => !isNaN(n)),
                          })
                        }
                        placeholder="200, 201, 202"
                      />
                    </div>
                    <TextArea
                      label="Response Mapping (JSON)"
                      value={
                        value.api?.success?.response_mapping
                          ? JSON.stringify(value.api.success.response_mapping, null, 2)
                          : ''
                      }
                      onChange={(text) => {
                        try {
                          const mapping = text.trim() ? JSON.parse(text) : {};
                          updateApiField('success', {
                            ...(value.api?.success || { status_codes: [200, 201, 202] }),
                            response_mapping: mapping,
                          });
                        } catch {
                          // Invalid JSON
                        }
                      }}
                      placeholder='{"provider_url": "data.generated_url"}'
                      rows={3}
                      hint="Map response fields to context_json keys"
                    />
                  </div>
                </div>
              )}

              {/* Clear Button */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-clear"
                  onClick={handleClear}
                >
                  Clear Configuration
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default RedemptionConfigEditor;
